import { CashFlow } from '@webcarrot/xirr';
import { calculatePoolMoic, transactionsToCashflows } from '@foresightdata/metrics-calculator';
import { startOfDay } from 'date-fns';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import { calculatePoolIrr } from '../../../providers/calculatePoolIrr';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';

export interface ICalculatedMetricsByUser {
  irr: number | null;
  moic: number | undefined;
  userId: number;
}

export interface IRawMetricsByUser {
  amountInvested: number;
  cashflows: CashFlow[];
  distributions: number;
  escrowAmount: number;
  fmv: number;
}

export function computeData(
  metrics: IMetricsDataModel[],
  asOfDate: Date,
  getCompany: (id: number) => ICompanyDataModel | null
) {
  const transactionsByUsers = metrics.reduce((res, metric) => {
    const company = getCompany(metric.companyId);
    if (company) {
      const { dealLeadId, internalSourceId } = company;
      addMetricToUser(res, metric, dealLeadId);
      addMetricToUser(res, metric, internalSourceId);
    }

    return res;
  }, new Map<number, IRawMetricsByUser>());

  const metricsByUsers: ICalculatedMetricsByUser[] = [];
  for (const [userId, data] of transactionsByUsers) {
    metricsByUsers.push({
      irr: calculatePoolIrr(data.cashflows, data.fmv, startOfDay(asOfDate)),
      moic: calculatePoolMoic(data),
      userId,
    });
  }

  return metricsByUsers;
}

function addMetricToUser(
  res: Map<number, IRawMetricsByUser>,
  metric: IMetricsDataModel,
  userId: number | null
) {
  if (userId == null) {
    return res;
  }
  if (!res.has(userId)) {
    res.set(userId, {
      amountInvested: 0,
      cashflows: [],
      distributions: 0,
      escrowAmount: 0,
      fmv: 0,
    });
  }

  const userMetrics = res.get(userId)!;
  for (const cashflow of transactionsToCashflows(metric.transactions)) {
    userMetrics.cashflows.push(cashflow);
  }

  userMetrics.amountInvested += metric.amountInvested;
  userMetrics.distributions += metric.distributions;
  userMetrics.escrowAmount += metric.escrowAmount;
  userMetrics.fmv += metric.fmv;

  return res;
}
